/* Product image zoom — ported from the C4 theme (component-product-image-zoom.js) to this theme's gallery.
   Desktop (mouse): hover zooms in place and follows the cursor; only the central 85% of the image steers,
   so the edges are easy to reach. Click opens the theme's PhotoSwipe viewer as before.
   Touch: tap zooms inside the image box and dragging pans; tap again (or tap elsewhere) zooms out.
   The small magnifier button still opens the fullscreen viewer.
   Zoom starts instantly with the image already on screen, then swaps to the high-res file once decoded. */
if (!customElements.get('product-image-zoom')) {
  class ProductImageZoom extends HTMLElement {
    static canHover() {
      return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    }

    connectedCallback() {
      this.lens = this.querySelector('.product-image-zoom__lens');
      if (!this.lens || this._ready) return;
      this._ready = true;

      this.style.setProperty('--zoom-scale', ProductImageZoom.scale);
      this.slide = this.closest('.product-main-slide');
      this.wrap = this.closest('.product-image-main') || this.parentElement;

      this.addEventListener('mouseenter', (e) => this.onMouseEnter(e));
      this.addEventListener('mousemove', (e) => this.onMouseMove(e));
      this.addEventListener('mouseleave', () => this.onMouseLeave());
      this.addEventListener('click', (e) => this.onClick(e));

      this.addEventListener('pointerdown', (e) => this.onPointerDown(e));
      this.addEventListener('pointermove', (e) => this.onPointerMove(e));
      this.addEventListener('pointerup', (e) => this.onPointerUp(e));
      this.addEventListener('pointercancel', () => { this._touch = null; });

      this.onOutsidePointer = (e) => {
        if (!this.contains(e.target)) this.touchZoomOut();
      };
      this.onWindowPointerUp = () => { this._mouseDown = false; };
      window.addEventListener('pointerup', this.onWindowPointerUp);
    }

    disconnectedCallback() {
      window.removeEventListener('pointerup', this.onWindowPointerUp);
      document.removeEventListener('pointerdown', this.onOutsidePointer, true);
    }

    // Slides that aren't the selected one (e.g. the mobile "partial" layout peek) shouldn't zoom
    isSelectedSlide() {
      const slider = this.closest('.flickity-enabled');
      return !slider || !this.slide || this.slide.classList.contains('is-selected');
    }

    /* ---------- Desktop: hover lens ---------- */

    onMouseEnter(e) {
      if (!ProductImageZoom.canHover() || !this.isSelectedSlide()) return;
      this.hovering = true;
      this._showLens();
      this.onMouseMove(e);
    }

    onMouseMove(e) {
      if (!ProductImageZoom.canHover()) return;
      if (this._mouseDown) return;
      if (!this.hovering) {
        // Pointer was already over the image when a drag ended or the slide changed
        if (this.isSelectedSlide()) this.onMouseEnter(e);
        return;
      }
      this._setOrigin(this._mapToOrigin(e.clientX, e.clientY));
    }

    onMouseLeave() {
      if (this.touchZoomed) return;
      this.hovering = false;
      this._lensOff();
    }

    onClick(e) {
      // Touch taps are handled in onPointerUp
      if (this._lastPointerType && this._lastPointerType !== 'mouse') return;
      if (!this.isSelectedSlide()) return;
      e.preventDefault();
      this.onMouseLeave();
      this.openViewer();
    }

    // Hand off to the theme's existing PhotoSwipe trigger for this image
    openViewer() {
      const trigger = this.wrap.querySelector('.js-photoswipe__zoom');
      if (trigger) trigger.click();
    }

    /* ---------- Pointer handling (mouse press + touch tap/pan) ---------- */

    onPointerDown(e) {
      this._lastPointerType = e.pointerType;
      if (e.pointerType === 'mouse') {
        // Hide the lens while the shopper might be dragging the slideshow
        this._mouseDown = true;
        this.hovering = false;
        this._lensOff();
        return;
      }
      if (!e.isPrimary) return;
      this._touch = {
        x: e.clientX,
        y: e.clientY,
        t: Date.now(),
        moved: false,
        origin: { ...(this._origin || { x: 50, y: 50 }) },
      };
      // While zoomed, keep Flickity from treating the pan as a swipe
      if (this.touchZoomed) e.stopPropagation();
    }

    onPointerMove(e) {
      const t = this._touch;
      if (!t || e.pointerType === 'mouse' || !e.isPrimary) return;
      const dx = e.clientX - t.x;
      const dy = e.clientY - t.y;
      if (Math.abs(dx) > 8 || Math.abs(dy) > 8) t.moved = true;
      if (!this.touchZoomed) return;

      e.stopPropagation();
      // Move the image with the finger: shifting the origin by d moves the content by d * (scale - 1)
      const rect = this.getBoundingClientRect();
      const k = ProductImageZoom.scale - 1;
      this._setOrigin({
        x: t.origin.x - (dx / (rect.width * k)) * 100,
        y: t.origin.y - (dy / (rect.height * k)) * 100,
      });
    }

    onPointerUp(e) {
      const t = this._touch;
      this._touch = null;
      if (!t || e.pointerType === 'mouse') return;
      if (this.touchZoomed) e.stopPropagation();
      // A tap: short and (almost) stationary. Swipes stay with the slideshow.
      if (t.moved || Date.now() - t.t > 500) return;
      if (this.touchZoomed) this.touchZoomOut();
      else if (this.isSelectedSlide()) this.touchZoomIn(e);
    }

    touchZoomIn(point) {
      this.touchZoomed = true;
      this.classList.add('product-image-zoom--touch-zoomed');
      this._setOrigin(this._mapToOrigin(point.clientX, point.clientY));
      this._showLens();
      document.addEventListener('pointerdown', this.onOutsidePointer, true);
    }

    touchZoomOut() {
      if (!this.touchZoomed) return;
      this.touchZoomed = false;
      this.classList.remove('product-image-zoom--touch-zoomed');
      this._lensOff();
      document.removeEventListener('pointerdown', this.onOutsidePointer, true);
    }

    /* ---------- Lens ---------- */

    // Only the central 85% of the image steers the zoom
    _mapToOrigin(clientX, clientY) {
      const area = 0.85;
      const inset = (1 - area) / 2;
      const rect = this.getBoundingClientRect();
      return {
        x: (((clientX - rect.left) / rect.width - inset) / area) * 100,
        y: (((clientY - rect.top) / rect.height - inset) / area) * 100,
      };
    }

    // Place the zoomed image so the point at origin (% of the image) lines up with the same % of the box
    _setOrigin(o) {
      this._origin = { x: Math.min(Math.max(o.x, 0), 100), y: Math.min(Math.max(o.y, 0), 100) };
      const k = ProductImageZoom.scale - 1;
      this.lens.style.setProperty('--zoom-tx', `${(-this._origin.x / 100) * this.offsetWidth * k}px`);
      this.lens.style.setProperty('--zoom-ty', `${(-this._origin.y / 100) * this.offsetHeight * k}px`);
      this.lens.style.setProperty('--zoom-ox', `${this._origin.x}%`);
      this.lens.style.setProperty('--zoom-oy', `${this._origin.y}%`);
    }

    _lensOn() {
      this.classList.add('product-image-zoom--active');
      clearTimeout(this._settleTimer);
      this._settleTimer = setTimeout(() => this.classList.add('product-image-zoom--settled'), 220);
    }

    _lensOff() {
      clearTimeout(this._settleTimer);
      this.classList.remove('product-image-zoom--settled', 'product-image-zoom--active');
    }

    _showLens() {
      if (!this.lens.getAttribute('src')) {
        const base = this.wrap.querySelector('img:not(.product-image-zoom__lens)');
        if (base && base.complete && base.naturalWidth > 0) {
          this.lens.src = base.currentSrc || base.src;
        }
      }
      if (this.lens.getAttribute('src')) this._lensOn();

      if (!this._hiResRequested && this.dataset.image) {
        this._hiResRequested = true;
        const hiRes = new Image();
        const swap = () => {
          this.lens.src = hiRes.src;
          this.classList.add('product-image-zoom--hi-res');
          if ((this.hovering || this.touchZoomed) && !this.classList.contains('product-image-zoom--active')) this._lensOn();
        };
        // decode() first so the swap doesn't flash a blank frame
        hiRes.onload = () => (hiRes.decode ? hiRes.decode().then(swap, swap) : swap());
        hiRes.src = this.dataset.image;
      }
    }
  }

  // Zoom level; passed to the CSS as --zoom-scale
  ProductImageZoom.scale = 2.5;

  customElements.define('product-image-zoom', ProductImageZoom);
}
